/**
 * Phase 7 Gelombang 2 — Sensitivity + Break-Even + PDF Report panels for Scenario Detail.
 */
import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Zap, Activity, FileDown } from "lucide-react";

const fmtRp = (x) => x === null || x === undefined ? "—" :
  `Rp ${new Intl.NumberFormat("id-ID").format(Math.round(x))}`;

const DRIVER_LABEL = {
  revenue_growth_percent: "Pertumbuhan Omzet",
  gross_margin_percent: "Gross Margin",
  ar_days: "AR Days",
  ap_days: "AP Days",
  inventory_days: "Inventory Days",
  opex_change_percent: "OpEx Change",
  tax_rate_percent: "Tax Rate",
};

export default function ScenarioAnalysisPanels({ scenarioId }) {
  const [sens, setSens] = useState(null);
  const [be, setBe] = useState(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!scenarioId) return;
    api.get(`/executive/scenarios/${scenarioId}/sensitivity`)
      .then(({ data }) => setSens(data))
      .catch(() => {});
    api.get(`/executive/scenarios/${scenarioId}/break-even`)
      .then(({ data }) => setBe(data))
      .catch(() => {});
  }, [scenarioId]);

  const downloadPdf = async () => {
    setWorking(true);
    try {
      const resp = await api.get(`/executive/scenarios/${scenarioId}/planning-report`,
        { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `planning-report-${scenarioId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Laporan PDF diunduh.");
    } catch (e) { toast.error(formatApiError(e)); } finally { setWorking(false); }
  };

  return (
    <div className="space-y-4" data-testid="scenario-analysis-panels">
      {/* Sensitivity */}
      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Zap className="h-4 w-4 text-amber-600" /> Analisis Sensitivitas (±5% / ±10%)
          </CardTitle>
          <Button variant="outline" size="sm" onClick={downloadPdf} disabled={working}
                   data-testid="btn-planning-pdf">
            <FileDown size={11} className="mr-1" />Unduh Laporan PDF
          </Button>
        </CardHeader>
        <CardContent>
          {!sens ? (
            <div className="text-xs text-slate-400 italic">Memuat…</div>
          ) : (
            <div className="space-y-4" data-testid="sens-grid">
              {sens.drivers.map((d) => (
                <div key={d.driver} data-testid={`sens-${d.driver}`}>
                  <div className="text-xs font-semibold mb-1">
                    {DRIVER_LABEL[d.driver] || d.driver}
                    <span className="text-slate-500 ml-2">base = {d.base_value ?? "—"}</span>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-right">Step</TableHead>
                        <TableHead className="text-right">Nilai Driver</TableHead>
                        <TableHead className="text-right">Net Profit</TableHead>
                        <TableHead className="text-right">ΔNP</TableHead>
                        <TableHead className="text-right">Ending Cash</TableHead>
                        <TableHead className="text-right">ΔCash</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {d.points.map((p) => (
                        <TableRow key={p.step_percent} data-testid={`sens-${d.driver}-${p.step_percent}`}>
                          <TableCell className="text-right text-xs font-mono">{p.step_percent}%</TableCell>
                          <TableCell className="text-right text-xs">{p.driver_value ?? "—"}</TableCell>
                          <TableCell className="text-right text-xs">{fmtRp(p.net_profit)}</TableCell>
                          <TableCell className={`text-right text-xs ${p.delta_net_profit > 0 ? "text-emerald-700" : p.delta_net_profit < 0 ? "text-rose-700" : ""}`}>
                            {fmtRp(p.delta_net_profit)}
                          </TableCell>
                          <TableCell className="text-right text-xs">{fmtRp(p.ending_cash)}</TableCell>
                          <TableCell className={`text-right text-xs ${p.delta_ending_cash > 0 ? "text-emerald-700" : p.delta_ending_cash < 0 ? "text-rose-700" : ""}`}>
                            {fmtRp(p.delta_ending_cash)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ))}
              <div className="text-[11px] text-slate-500 italic">{sens.note}</div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Break-even */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Activity className="h-4 w-4 text-sky-700" /> Titik Impas (Break-even)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!be ? (
            <div className="text-xs text-slate-400 italic">Memuat…</div>
          ) : !be.available ? (
            <div className="text-xs text-amber-700 italic" data-testid="be-not-available">
              {be.note || "Data belum cukup untuk analisis titik impas"}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs" data-testid="be-summary">
              <div>
                <div className="text-slate-500">Contribution Margin</div>
                <div className="font-semibold">{fmtRp(be.contribution_margin)}</div>
                <div className="text-[10px] text-slate-500">{be.contribution_margin_ratio_percent}%</div>
              </div>
              <div>
                <div className="text-slate-500">Fixed Cost (OpEx)</div>
                <div className="font-semibold">{fmtRp(be.fixed_cost)}</div>
              </div>
              <div>
                <div className="text-slate-500">Break-even Revenue</div>
                <div className="font-semibold text-sky-700">{fmtRp(be.break_even_revenue)}</div>
              </div>
              <div>
                <div className="text-slate-500">Rasio vs Actual</div>
                <div className="font-semibold">{be.break_even_vs_actual_ratio}×</div>
              </div>
              <div className="col-span-full text-[11px] text-slate-500 italic">{be.note}</div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
